import { CheckCircle2, Printer, ReceiptText } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  fmtAmount,
  fmtWhen,
  text,
  type ApiRecord,
} from "@/features/finance/kit";

type ReceiptTicketProps = {
  transaction: ApiRecord;
  amount: unknown;
  currency: string;
  reference: string;
  operation: string;
  paymentMethod?: string;
  phone?: string;
  country?: string;
  merchant?: string;
};

const escapeHtml = (value: unknown) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        character
      ]!,
  );

export function ReceiptTicket({
  transaction,
  amount,
  currency,
  reference,
  operation,
  paymentMethod,
  phone,
  country,
  merchant,
}: ReceiptTicketProps) {
  const rows = [
    [
      "Référence",
      text(transaction, "reference", "payment_reference") || reference,
    ],
    ["Opération", text(transaction, "operation") || operation],
    [
      "Moyen de paiement",
      text(transaction, "payment_method", "operator") || paymentMethod,
    ],
    [
      "Téléphone",
      text(transaction, "phone_number", "payer.phone_number") || phone,
    ],
    ["Pays", text(transaction, "country") || country],
    [
      "Frais",
      transaction.fee_amount ? fmtAmount(transaction.fee_amount, currency) : "",
    ],
    [
      "Montant net",
      transaction.net_amount ? fmtAmount(transaction.net_amount, currency) : "",
    ],
    ["Référence prestataire", text(transaction, "provider_reference")],
    [
      "Date",
      fmtWhen(
        transaction.completed_at ??
          transaction.created_at ??
          new Date().toISOString(),
      ),
    ],
  ].filter(([, value]) => Boolean(value));

  function printReceipt() {
    const printWindow = window.open("", "_blank", "width=420,height=720");
    if (!printWindow) return;
    printWindow.document
      .write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Reçu ${escapeHtml(reference)}</title><style>
      *{box-sizing:border-box}body{margin:0;padding:28px;background:#f1f5f9;color:#10213d;font:14px/1.45 Arial,sans-serif}.receipt{position:relative;max-width:380px;margin:0 auto;padding:26px 24px;background:#fff;border:1px solid #dce5f1;border-radius:18px;box-shadow:0 12px 32px #132b4d12}.brand{display:flex;align-items:center;gap:10px;padding-bottom:18px;border-bottom:1px dashed #cbd5e1}.mark{display:grid;place-items:center;width:38px;height:38px;border-radius:12px;background:#eaf3ff;color:#0866ff}.brand b{font-size:16px}.success{display:flex;justify-content:center;margin:23px 0 8px;color:#059669}.eyebrow{text-align:center;color:#64748b;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase}.amount{text-align:center;margin:5px 0 4px;font-size:30px;font-weight:800;letter-spacing:-.04em}.status{text-align:center;color:#047857;font-size:12px;font-weight:700}.merchant{text-align:center;margin-top:8px;color:#64748b;font-size:12px}.rule{height:1px;margin:20px 0;border-top:1px dashed #cbd5e1}.row{display:flex;justify-content:space-between;gap:18px;padding:9px 0;border-bottom:1px solid #eef2f7}.row:last-child{border-bottom:0}.label{color:#64748b}.value{text-align:right;max-width:62%;font-weight:700;overflow-wrap:anywhere}.footer{margin-top:21px;text-align:center;color:#94a3b8;font-size:10px}.print{position:absolute;top:-9999px}
      @media print{body{padding:0;background:white}.receipt{max-width:none;border:0;box-shadow:none;border-radius:0}}
    </style></head><body><article class="receipt"><div class="brand"><span class="mark">BCP</span><b>BizConnectPay</b></div><div class="success">✓</div><div class="eyebrow">Reçu de paiement</div><div class="amount">${escapeHtml(fmtAmount(amount, currency))}</div><div class="status">Paiement confirmé</div>${merchant ? `<div class="merchant">Payé à ${escapeHtml(merchant)}</div>` : ""}<div class="rule"></div>${rows.map(([label, value]) => `<div class="row"><span class="label">${escapeHtml(label)}</span><span class="value">${escapeHtml(value)}</span></div>`).join("")}<div class="footer">Reçu électronique · BizConnectPay</div></article><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close()}</script></body></html>`);
    printWindow.document.close();
  }

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm animate-in fade-in slide-in-from-top-2 duration-500"
      data-testid="payment-receipt"
      aria-label="Reçu de paiement"
    >
      <div className="flex items-center gap-3 border-b border-dashed border-slate-200 pb-4">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-blue-600">
          <ReceiptText className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-extrabold text-slate-900">BizConnectPay</p>
          <p className="text-[11px] text-slate-500">Reçu de paiement</p>
        </div>
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
      </div>
      <div className="py-5 text-center">
        <p className="text-[10px] font-bold uppercase tracking-[.16em] text-slate-500">
          Paiement confirmé
        </p>
        <p className="mt-1 text-3xl font-black tracking-tight text-slate-950">
          {fmtAmount(amount, currency)}
        </p>
        {merchant && (
          <p className="mt-1 text-xs text-slate-500">Payé à {merchant}</p>
        )}
      </div>
      <div className="space-y-2 border-t border-dashed border-slate-200 pt-3 text-left text-xs">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4">
            <span className="text-slate-500">{label}</span>
            <span className="max-w-[62%] break-all text-right font-semibold text-slate-800">
              {value}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-end border-t border-dashed border-slate-200 pt-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={printReceipt}
          className="gap-2"
          data-testid="button-print-receipt"
        >
          <Printer className="h-4 w-4" /> Imprimer / Enregistrer
        </Button>
      </div>
    </section>
  );
}
